// pages/NewThread.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiFetch } from "../api";
import "../styles/pages/_new-thread.css";

export default function NewThread() {
    const { categoryId }          = useParams();
    const navigate                = useNavigate();
    const [categoryName, setCategoryName] = useState("");
    const [title, setTitle]       = useState("");
    const [content, setContent]   = useState("");
    const [preview, setPreview]   = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError]       = useState(null);

    // Fetch category name for breadcrumb
    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const data = await apiFetch(`/api/forum/categories/${categoryId}`);
                if (!cancelled) setCategoryName(data.category.name);
            } catch { }
        }
        load();
        return () => { cancelled = true; };
    }, [categoryId]);

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);

        if (!title.trim()) { setError("Title is required."); return; }
        if (!content.trim()) { setError("Post body is required."); return; }

        setSubmitting(true);
        try {
            const data = await apiFetch(`/api/forum/categories/${categoryId}/threads`, {
                method: "POST",
                body: JSON.stringify({ title: title.trim(), content: content.trim() }),
            });
            // Redirect
            navigate(`/thread/${data.thread.id}`);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="wrapper new-thread">
            <div className="new-thread__breadcrumb">
                <Link to="/forum">Forum</Link>
                <span className="new-thread__breadcrumb-sep">›</span>
                <Link to={`/forum/${categoryId}`}>
                    {categoryName || `Category #${categoryId}`}
                </Link>
                <span className="new-thread__breadcrumb-sep">›</span>
                <span>New Thread</span>
            </div>

            <h1 className="new-thread__title">Create New Thread</h1>

            {error && <div className="new-thread__error">{error}</div>}

            <form className="new-thread__form" onSubmit={handleSubmit}>
                <div className="new-thread__field">
                    <label htmlFor="thread-title" className="new-thread__label">
                        Thread Title - max 50 characters
                    </label>
                    <input
                        id="thread-title"
                        type="text"
                        className="new-thread__input"
                        placeholder="Give your thread a title…"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={50}
                        disabled={submitting}
                    />
                </div>

                <div className="new-thread__field">
                    <div className="new-thread__editor-header">
                        <label className="new-thread__label">Post Body</label>
                        <div className="new-thread__tab-bar">
                            <button
                                type="button"
                                className={`new-thread__tab ${!preview ? "new-thread__tab--active" : ""}`}
                                onClick={() => setPreview(false)}
                            >
                                Write
                            </button>
                            <button
                                type="button"
                                className={`new-thread__tab ${preview ? "new-thread__tab--active" : ""}`}
                                onClick={() => setPreview(true)}
                            >
                                Preview
                            </button>
                        </div>
                    </div>

                    {preview ? (
                        <div className="new-thread__preview markdown-body">
                            {content.trim() ? (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {content}
                                </ReactMarkdown>
                            ) : (
                                <p className="new-thread__preview-empty">Nothing to preview.</p>
                            )}
                        </div>
                    ) : (
                        <textarea
                            className="new-thread__textarea"
                            placeholder="Write your post using Markdown…"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            maxLength={1000}
                            rows={12}
                            disabled={submitting}
                        />
                    )}

                    <p className="new-thread__hint">
                        Supports Markdown: **bold**, *italic*, `code`, lists, links, tables, etc.
                    </p>
                </div>

                <div className="new-thread__actions">
                    <Link
                        to={`/forum/${categoryId}`}
                        className="btn"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        className="btn btn--danger"
                        disabled={submitting}
                    >
                        {submitting ? "Creating…" : "Create Thread"}
                    </button>
                </div>
            </form>
        </div>
    );
}